import {
  Box, Button, Grid, SelectChangeEvent, Typography,
} from '@mui/material';
import React, { useMemo } from 'react';
import {
  AreaPlot,
  ChartsReferenceLine,
  ChartsXAxis, ChartsYAxis, LinePlot, MarkPlot, ResponsiveChartContainer, ChartsTooltip, ChartsAxisHighlight,
} from '@mui/x-charts';
import dayjs from 'dayjs';
import { useQuery, useLazyQuery } from '@apollo/client';
import csvDownload from 'json-to-csv-export';
import EmptyPageMessage from './EmptyPageMessage';
import { colors } from '../../theme';
import DateFilter from '../DateFilter';
import FeedbackFiltersDialog from '../FeedbackFiltersDialog';
import ExperienceGraphsLoader from './ExperienceGraphsLoader';
import { GET_EXPERIENCE_TAB } from '../../queries';
import SurveyResponseCard from './SurveyResponseCard';

function ExperienceGraphsTab({
  awoId,
  awoName,
  handleCardClick,
  adopterTypeformId,
  fosterTypeformId,
}: {
  awoId: string,
  awoName: string,
  handleCardClick: (id: number) => void,
  adopterTypeformId: string,
  fosterTypeformId: string,
}) {
  const [daysAgo, setDaysAgo] = React.useState(-1);
  const [filterDialogOpen, setFilterDialogOpen] = React.useState(false);
  const [appliedFilters, setAppliedFilters] = React.useState<Record<string, boolean>>({
    dogs: true,
    cats: true,
    adopted: true,
    fostered: true,
  });
  const [offset, setOffset] = React.useState(0);
  const [hasMore, setHasMore] = React.useState(true);
  const [downloadQuery, { loading: downloadLoading }] = useLazyQuery(GET_EXPERIENCE_TAB);

  // Map appliedFilters to careType and animalType (same as DayTenTab)
  const mappedFilters = React.useMemo(() => {
    let careType = null;
    let animalType = null;

    // Map species filters to careType
    if (appliedFilters.dogs && !appliedFilters.cats) {
      animalType = 'Dog';
    } else if (appliedFilters.cats && !appliedFilters.dogs) {
      animalType = 'Cat';
    }

    // Map relationship filters to animalType
    if (appliedFilters.adopted && !appliedFilters.fostered) {
      careType = 'Adopted';
    } else if (appliedFilters.fostered && !appliedFilters.adopted) {
      careType = 'Foster';
    }

    return { careType, animalType };
  }, [appliedFilters]);

  const {
    loading,
    data,
    fetchMore,
    refetch,
  } = useQuery(GET_EXPERIENCE_TAB, {
    variables: {
      awoId,
      limit: 6,
      offset: 0,
      typeformIds: (() => {
        // Determine which typeform IDs to use based on careType
        let typeformIdsToUse;
        if (mappedFilters.careType === 'Adopted') {
          typeformIdsToUse = [adopterTypeformId];
        } else if (mappedFilters.careType === 'Foster') {
          typeformIdsToUse = [fosterTypeformId];
        } else {
          // If both or neither are selected, use both IDs
          typeformIdsToUse = [adopterTypeformId, fosterTypeformId];
        }
        return typeformIdsToUse;
      })(),
      daysAgo,
      startDate: dayjs().subtract(12, 'months').format('YYYY-MM-DD'),
      endDate: dayjs().format('YYYY-MM-DD'),
      orderBy: 'desc',
      careType: mappedFilters.careType,
      animalType: mappedFilters.animalType,
    },
    skip: !awoId,
  });

  // Refetch when daysAgo or appliedFilters changes
  React.useEffect(() => {
    if (awoId) {
      setOffset(0);
      setHasMore(true);

      // Determine which typeform IDs to use based on careType
      let typeformIdsToUse;
      if (mappedFilters.careType === 'Adopted') {
        typeformIdsToUse = [adopterTypeformId];
      } else if (mappedFilters.careType === 'Foster') {
        typeformIdsToUse = [fosterTypeformId];
      } else {
        // If both or neither are selected, use both IDs
        typeformIdsToUse = [adopterTypeformId, fosterTypeformId];
      }

      refetch({
        awoId,
        limit: 6,
        offset: 0,
        typeformIds: typeformIdsToUse,
        daysAgo,
        startDate: dayjs().subtract(12, 'months').format('YYYY-MM-DD'),
        endDate: dayjs().format('YYYY-MM-DD'),
        orderBy: 'desc',
        careType: mappedFilters.careType,
        animalType: mappedFilters.animalType,
      });
    }
  }, [daysAgo, appliedFilters, awoId, refetch, adopterTypeformId, fosterTypeformId, mappedFilters]);

  React.useEffect(() => {
    if (data?.responses.length < 6) {
      setHasMore(false);
    }
  }, [data]);

  const npsDataToChart = useMemo(() => {
    if (!data) return [];
    const mappedToDate = data.npsOverTime.map((nps) => ({
      value: nps.score,
      date: dayjs(nps.month).valueOf(),
    }));
    return mappedToDate.sort((a, b) => a.date - b.date);
  }, [data]);

  const handleShowMore = () => {
    const newOffset = offset + 6;
    fetchMore({
      variables: {
        offset: newOffset,
        limit: 6,
        careType: mappedFilters.careType,
        animalType: mappedFilters.animalType,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || fetchMoreResult.responses.length < 6) {
          setHasMore(false);
        }
        setOffset(newOffset);

        return {
          responses: [
            ...prev.responses,
            ...fetchMoreResult.responses,
          ],
        };
      },
    });
  };

  const downloadHandler = () => {
    // Question title normalization mapping
    const normalizeQuestion = (question: string) => {
      let mappedQuestion = null;
      if (question.startsWith('How likely are you to recommend')) {
        mappedQuestion = 'How likely are you to recommend the organization?';
      } else if (question.startsWith('Was there anything that could have made the experience better?')) {
        mappedQuestion = 'Was there anything that could have made the experience better?';
      } else if (question.startsWith('Please elaborate')) {
        mappedQuestion = 'Please elaborate';
      } else if (question.startsWith('Did you have any concerns')) {
        mappedQuestion = 'Did you have any concerns prior to bringing the animal home?';
      } else if (question.includes('did particularly well')) {
        mappedQuestion = 'Was there anything that the organization did particularly well?';
      } else if (question.includes('Please share what')) {
        mappedQuestion = 'Please share what made your experience great.';
      } else if (question.includes('Last question! Would you be interested')) {
        mappedQuestion = 'Would you be interested in helping the organization as a foster, volunteer or donor in the future?';
      } else if (question.includes('Great! Let us know')) {
        mappedQuestion = 'How would you be interested in helping?';
      } else if (question.includes('Last question! How supported')) {
        mappedQuestion = 'How supported did you feel while fostering?';
      }
      // Add more conditions as needed
      return mappedQuestion;
    };

    downloadQuery({
      variables: {
        awoId,
        limit: 100,
        offset: 0,
        typeformIds: (() => {
          // Determine which typeform IDs to use based on careType
          let typeformIdsToUse;
          if (mappedFilters.careType === 'Adopted') {
            typeformIdsToUse = [adopterTypeformId];
          } else if (mappedFilters.careType === 'Foster') {
            typeformIdsToUse = [fosterTypeformId];
          } else {
            // If both or neither are selected, use both IDs
            typeformIdsToUse = [adopterTypeformId, fosterTypeformId];
          }
          return typeformIdsToUse;
        })(),
        startDate: dayjs().subtract(12, 'months').format('YYYY-MM-DD'),
        endDate: dayjs().format('YYYY-MM-DD'),
        orderBy: 'desc',
        careType: mappedFilters.careType,
        animalType: mappedFilters.animalType,
      },
    }).then((result) => {
      const downloadData = result.data.responses.map((response: any) => {
        const baseData = {
          Email: response.petParent.email,
          'Animal Name': response.petParent.animals[0].name,
          'Animal Species': response.petParent.animals[0].animalType,
          'Animal Care Type': response.petParent.animals[0].careType,
          'Date of Submission': response.submittedAt,
        };

        // Flatten answers into individual columns with normalized titles
        const answerData = response.answers.reduce((acc: any, answer: any) => {
          const normalizedQuestion = normalizeQuestion(answer.question);
          if (normalizedQuestion && answer.text) {
            // eslint-disable-next-line no-nested-ternary
            acc[`"${normalizedQuestion}"`] = answer.text.toLowerCase() === 'true' ? 'Yes' : (answer.text.toLowerCase() === 'false' ? 'No' : answer.text);
          }
          return acc;
        }, {});

        return { ...baseData, ...answerData };
      });

      console.log(downloadData);

      csvDownload({
        data: downloadData,
        filename: `Experience_Surveys_${new Date().toISOString()}.csv`,
      });
    });
  };

  return (
    !loading && data?.responses.length === 0 && data?.npsOverTime.length === 0 ? (
      <EmptyPageMessage />
    ) : (
      <>
        <Box
          sx={{
            px: 3,
          }}
        >
          {loading && <ExperienceGraphsLoader />}
          {
            !loading && (
              <>
                <Box
                  sx={{
                    py: 2,
                  }}
                >
                  <Typography variant="h5">Experience Surveys</Typography>
                  <Typography sx={{ mt: 1 }} variant="body2">
                    {`We use Net Promoter Score (NPS) to measure pet parent satisfaction
                    by asking fosters and adopters to rate how likely they are to
                    recommend ${awoName} on a scale of 0-10. Responses are categorized as
                    detractors (0-6), passives (7-8), and promoters (9-10). NPS is
                    calculated by subtracting the percentage of detractors from the
                    percentage of promoters. A good NPS is typically 30-50, with
                    anything above 70 considered excellent.`}
                  </Typography>
                </Box>
                <Box>
                  <ResponsiveChartContainer
                    height={320}
                    series={[
                      {
                        type: 'line',
                        dataKey: 'value',
                        label: 'NPS',
                        area: true,
                        color: colors.primary.main,
                      },
                    ]}
                    dataset={npsDataToChart as any}
                    xAxis={[
                      {
                        scaleType: 'point',
                        dataKey: 'date',
                        valueFormatter: (val) => dayjs(val).format('MMM'),
                      },
                    ]}
                    yAxis={[
                      {
                        min: 0,
                        max: 100,
                      },
                    ]}
                  >
                    <AreaPlot />
                    <ChartsYAxis position="right" />
                    <ChartsYAxis min={0} max={10} />
                    <LinePlot />
                    <MarkPlot />
                    <ChartsXAxis />
                    <ChartsTooltip trigger="axis" />
                    <ChartsAxisHighlight x="line" />
                  </ResponsiveChartContainer>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6">Survey Responses</Typography>
                  <Box
                    sx={{
                      borderColor: colors.grey.divider,
                      borderWidth: '1px',
                      borderStyle: 'solid',
                      px: 1,
                      py: 0.5,
                      borderRadius: '0.25rem',
                      display: 'flex',
                      gap: 1,
                    }}
                  >
                    <DateFilter
                      formControl={{
                        variant: 'standard',
                        sx: { bgcolor: 'transparent' },
                      }}
                      selectedDate={daysAgo}
                      onChange={(e: SelectChangeEvent<number>) => {
                        setDaysAgo(Number(e.target.value));
                      }}
                    />
                    <Button
                      variant="text"
                      size="small"
                      color="black"
                      onClick={() => setFilterDialogOpen(true)}
                    >
                      Filters
                    </Button>
                    <Button
                      disabled={downloadLoading}
                      onClick={downloadHandler}
                      variant="text"
                      size="small"
                      color="black"
                    >
                      Download this data
                    </Button>
                  </Box>
                </Box>
                <Box sx={{ mx: -1 }}>
                  <Grid container spacing={2} sx={{ py: 2 }}>
                    {data?.responses.map((response) => (
                      <Grid
                        item
                        xs={6}
                        lg={4}
                        key={response.id}
                      >
                        <SurveyResponseCard
                          response={response}
                          onClick={() => handleCardClick(response.petParent.id)}
                        />
                      </Grid>
                    ))}
                    {data?.responses.length === 0 && (
                      <Grid item xs={12}>
                        <EmptyPageMessage />
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </>
            )
          }
        </Box>
        {!loading && data?.responses.length > 0 && hasMore && (
          <Box sx={{ textAlign: 'center' }}>
            <Button
              type="button"
              variant="text"
              onClick={handleShowMore}
            >
              Show more
            </Button>
          </Box>
        )}
        <FeedbackFiltersDialog
          open={filterDialogOpen}
          setOpen={setFilterDialogOpen}
          appliedFilters={appliedFilters}
          setAppliedFilters={setAppliedFilters}
          showCareType
          showAnimalType
        />
      </>
    )
  );
}

export default ExperienceGraphsTab;
